/**
 * Runtime client env.
 *
 * Reads values injected by the server at request time as `window.__APP_ENV__`
 * (see resources/views/app.blade.php), falling back to Vite's build-time
 * `import.meta.env.VITE_*` for local dev. This keeps deployment-specific values
 * (tldraw license, Sentry DSN, AI service URL, app name) OUT of the frozen
 * bundle, so a single built image serves any customer/domain — each deployment
 * drives them via its own env.
 *
 * SSR-safe: `window` is undefined in the SSR bundle, so it transparently falls
 * back to the build-time env there (the runtime consumers — Sentry, tldraw —
 * are browser-only anyway).
 */
type RuntimeEnvKey =
    | 'VITE_APP_NAME'
    | 'VITE_TLDRAW_LICENSE_KEY'
    | 'VITE_AI_SERVICE'
    | 'VITE_SENTRY_DSN'
    | 'VITE_SENTRY_ENVIRONMENT'
    | 'VITE_SENTRY_SAMPLE_RATE';

type RuntimeEnv = Partial<Record<RuntimeEnvKey, string | null>>;

const runtime: RuntimeEnv =
    typeof window !== 'undefined' && (window as unknown as { __APP_ENV__?: RuntimeEnv }).__APP_ENV__
        ? (window as unknown as { __APP_ENV__: RuntimeEnv }).__APP_ENV__
        : {};

/**
 * Resolve a runtime client env value. Server-injected value wins; otherwise
 * fall back to the build-time Vite env (local dev). Empty string / null are
 * treated as "not set" so a blank injected value falls through to the dev env.
 */
export function runtimeEnv(key: RuntimeEnvKey): string | undefined {
    // Production: prefer server-injected runtime config. Dev: use local Vite env.
    if (import.meta.env.PROD) {
        const injected = runtime[key];
        if (injected !== undefined && injected !== null && injected !== '') {
            return injected;
        }
    }
    return import.meta.env[key] as string | undefined;
}
